import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useBlocker, useLocation, useNavigationType } from 'react-router-dom';
import { PopupDialog } from '@vigeo/ui';

/**
 * One group is edited at a time, and what was typed is never lost without being asked (#585; pop-up rules card 14).
 *
 * Everything that would take the form away goes through `request`: Cancel, Edit on another group, another tab, a link
 * inside the profile. Leaving the page by any link in the app is caught by the router. Each asks "Discard what you
 * typed?" when something was typed, and does it at once when nothing was. A reload or a closed tab gets the browser's own question.
 *
 * While a save is on its way nothing takes the form away and nothing is asked: the change has been sent, so "it will be lost"
 * would be untrue (review of #586, finding 4).
 */
export interface EditGuardValue {
  /** The group being edited, or null. */
  readonly editing: string | null;
  /** Open this group's form, asking first when another group has something typed. */
  readonly begin: (group: string) => void;
  /** Close the form without asking: it was saved, or nothing changed. */
  readonly end: () => void;
  /** The open form says whether anything in it differs from when it opened. */
  readonly setDirty: (dirty: boolean) => void;
  /** Do this once the open form is out of the way. Does nothing while a save is on its way. */
  readonly request: (action: () => void) => void;
  /** The open form says a save is on its way, and when it has come back. */
  readonly setBusy: (busy: boolean) => void;
}

const EditGuardContext = createContext<EditGuardValue | null>(null);

export function useEditGuard(): EditGuardValue {
  const guard = useContext(EditGuardContext);
  if (guard === null) throw new Error('useEditGuard needs an EditGuardProvider above it');
  return guard;
}

export function EditGuardProvider({ children }: { readonly children: ReactNode }) {
  const [editing, setEditing] = useState<string | null>(null);
  const [dirty, setDirtyState] = useState(false);
  // What was asked for while something typed stood in the way. Wrapped in an object, because React would call a bare function given to a setter.
  const [waiting, setWaiting] = useState<{ readonly run: () => void } | null>(null);
  // Read by the callbacks below, and written by them AS WELL AS by each render: Discard closes the form and then runs what was
  // waiting in the same tick, and that must already see the form as closed or it would ask its question a second time.
  const [busy, setBusyState] = useState(false);
  const state = useRef({ editing, dirty, busy });
  state.current = { editing, dirty, busy };

  const end = useCallback(() => { state.current = { editing: null, dirty: false, busy: false }; setEditing(null); setDirtyState(false); setBusyState(false); }, []);
  const request = useCallback((action: () => void) => {
    if (state.current.busy) return;
    if (state.current.editing !== null && state.current.dirty) { setWaiting({ run: action }); return; }
    end();
    action();
  }, [end]);
  const begin = useCallback((group: string) => request(() => { state.current = { editing: group, dirty: false, busy: false }; setEditing(group); }), [request]);
  const setDirty = useCallback((next: boolean) => { state.current = { ...state.current, dirty: next }; setDirtyState(next); }, []);
  const setBusy = useCallback((next: boolean) => { state.current = { ...state.current, busy: next }; setBusyState(next); }, []);

  /**
   * ANY move the router makes, not just a change of page.
   *
   * This used to compare PATHNAMES only, with the reasoning that a tab of this profile changes just
   * the query string and is asked through `request` instead. True of a tab CLICK, and false of the
   * browser's Back and Forward buttons, which traverse the very history entries those clicks pushed
   * without passing through any handler of ours (#586, reviewer 209 finding 1, proven in a browser).
   * The typed text went silently and the form came back open and EMPTY — reading as though nothing
   * had been typed rather than as though something had been dropped.
   *
   * So the question is the search as well. An in-app tab click is still not double-asked: `request`
   * runs `end()` before the navigation, so `state.current.dirty` is already false by the time this
   * is consulted. What is left for this to catch is exactly the history moves, plus leaving the
   * page, which it caught before.
   *
   * The hash is not compared because nothing on this page uses one; a fragment link would be a
   * reason to revisit, not a silent hole, since it cannot discard a form on its own.
   */
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    state.current.editing !== null
    && state.current.dirty
    && (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search));
  // Stopped while a save is on its way (a form that is saving always has something typed): stay, and ask nothing.
  useEffect(() => { if (blocker.state === 'blocked' && busy) blocker.reset(); }, [blocker, busy]);

  /**
   * Back or Forward that was NOT stopped (nothing was typed) closes the open form. Which group is open is this component's own
   * state, not part of the address, so without this, coming back to the tab showed the form open again and empty: the
   * nothing-typed half of finding 1. Only for the browser's own moves (`POP`): "Add" on the Overview moves to a tab and opens
   * a form there in one step.
   */
  const location = useLocation();
  const moveKind = useNavigationType();
  useEffect(() => { if (moveKind === 'POP') end(); }, [location.key, moveKind, end]);

  useEffect(() => {
    if (!(editing !== null && dirty)) return;
    const ask = (event: BeforeUnloadEvent) => { event.preventDefault(); };
    window.addEventListener('beforeunload', ask);
    return () => window.removeEventListener('beforeunload', ask);
  }, [editing, dirty]);

  const asking = waiting !== null || (blocker.state === 'blocked' && !busy);
  const keep = () => { setWaiting(null); if (blocker.state === 'blocked') blocker.reset(); };
  const discard = () => {
    const run = waiting?.run;
    setWaiting(null);
    end();
    if (blocker.state === 'blocked') blocker.proceed();
    run?.();
  };

  const value = useMemo(() => ({ editing, begin, end, setDirty, request, setBusy }), [editing, begin, end, setDirty, request, setBusy]);
  return (
    <EditGuardContext.Provider value={value}>
      {children}
      {/* The question as its own small dialog: Keep editing holds the keyboard, so Enter changes nothing. Esc, the X and a click outside keep editing too. */}
      <PopupDialog
        open={asking}
        title="Discard what you typed?"
        size="small"
        startOn="cancel"
        cancelLabel="Keep editing"
        primary={{ label: 'Discard', onClick: discard }}
        onClose={keep}
      >
        What you typed in this form will be lost.
      </PopupDialog>
    </EditGuardContext.Provider>
  );
}
